import type { PrimitiveTypeNumber } from '#layers/data-model/types/schema';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import {
	PRIMITIVE_TYPES_FLOAT,
	PRIMITIVE_TYPES_SIGNED_INTEGER,
	PRIMITIVE_TYPES_UNSIGNED_INTEGER,
} from '#layers/data-model/constants/schema/primitive-type';
import { getPrimitiveInputDefaults } from '#layers/data-model/utils/field-types/primitive-input-defaults';
import { defineFieldTypeTemplate } from '../define';

export interface NumberFieldOptions {
	min?: number | string;
	max?: number | string;
	step?: number | string;
	stepSnapping?: boolean;
}

const FULL_RANGE_TYPES = [...PRIMITIVE_TYPES_SIGNED_INTEGER, ...PRIMITIVE_TYPES_FLOAT, 'decimal'] as const;

export default defineFieldTypeTemplate<NumberFieldOptions>({
	type: 'primitive',
	id: 'number',
	name: '$t:field_types.number.name',
	description: '$t:field_types.number.description',
	icon: 'lucide:hash',
	sort: 100,
	keywords: ['integer', 'int', 'float', 'decimal', 'numeric', 'double'],
	supportedPrimitiveTypes: ({ options }) => {
		if (!options) {
			return [...FULL_RANGE_TYPES, ...PRIMITIVE_TYPES_UNSIGNED_INTEGER];
		}

		// the first type is the preselected type, choose int32 for that
		const types: PrimitiveTypeNumber['name'][] = [...FULL_RANGE_TYPES];

		// only allow unsigned primitive types if there is no negative minimum
		if (options.min === undefined || Number(options.min) >= 0) {
			types.push(...PRIMITIVE_TYPES_UNSIGNED_INTEGER);
		}

		return types;
	},
	options: ({ form, context: { primitiveType } }) => {
		const inputBlock = getPrimitiveInputDefaults(primitiveType);

		return form([
			{
				name: 'min',
				label: '$t:field_types.number.options.min.label',
				description: '$t:field_types.number.options.min.description',
				block: inputBlock.block,
				props: inputBlock.props,
			},
			{
				name: 'max',
				label: '$t:field_types.number.options.max.label',
				description: '$t:field_types.number.options.max.description',
				block: inputBlock.block,
				props: inputBlock.props,
			},
			{
				name: 'step',
				label: '$t:field_types.number.options.step.label',
				description: '$t:field_types.number.options.step.description',
				block: inputBlock.block,
				props: {
					...inputBlock.props,
					stepSnapping: false,
				},
			},
			{
				name: 'stepSnapping',
				label: '$t:field_types.number.options.step_snapping.label',
				description: '$t:field_types.number.options.step_snapping.description',
				block: BuiltinBlockIds.InputSwitch,
				default: () => false,
			},
		]);
	},
});
